/**
 * とうろく・ログイン画面(docs/10_paltown_platform.md §3.1)。
 *
 * ここは**通らなくてもゲームは遊べる**。導線はプロフィール欄の小さなボタン1つだけで、
 * 遊びの流れには一切割り込まない。
 *
 * 文言は小学生も読むのでひらがな中心だが、**操作するのは保護者**の想定なので
 * 冒頭に「おうちのひとと いっしょに」を必ず出す。
 *
 * ┌──────────────────────────────┐
 * │ [もどる]  とうろく・ログイン           │
 * │ ⚠️ おうちのひとと いっしょに やってね    │
 * │ ( はじめて とうろく | ログイン )       │
 * │  メールアドレス / パスワード → ボタン    │
 * │  ── または ──  [Google で つづける]   │
 * │ とうろくしなくても あそべるよ           │
 * └──────────────────────────────┘
 */

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { appServices } from '../services/appServices';
import { accountLabel, providerLabel } from '../services/auth';
import {
  formatCodeInput,
  isCompleteCode,
  transferCardMode,
  transferErrorMessage,
  TRANSFER_LABELS,
  welcomeBackText,
  type TransferCode,
} from '../services/transfer';
import { useAuthStore } from '../store/authStore';
import { useGameStore } from '../store/gameStore';
import './account.css';

type Tab = 'register' | 'signIn';

const MIN_PASSWORD_LENGTH = 6;

/**
 * ひきつぎカード(docs/10_paltown_platform.md §3.4)。
 *
 * ┌──────────────────────────────────────┐
 * │ 🔁 ひきつぎ                                  │
 * │ [ ひきつぎコードを つくる ][ …を いれる ]      │
 * │   ABCD-2345   7にちかん つかえるよ  [コピー]  │
 * │ ひきついだあとも、まえの ばしょの データは のこる│
 * └──────────────────────────────────────┘
 *
 * つくる側もいれる側も**ゲスト(匿名)だけ**。とうろくずみの子は
 * ログインすれば同じ UID になるので、一文だけ出して終わる。
 */
export function TransferCard({ openIssue = false }: { readonly openIssue?: boolean }) {
  const goTitle = useGameStore((state) => state.goTitle);
  const account = useAuthStore((auth) => auth.user);
  const transfer = appServices.transfer;

  const [panel, setPanel] = useState<'none' | 'issue' | 'redeem'>(openIssue ? 'issue' : 'none');
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState<TransferCode | null>(null);
  const [copied, setCopied] = useState(false);
  const [input, setInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [welcome, setWelcome] = useState<string | null>(null);
  // 旧URLの案内から来たとき、カードまで自動でスクロールする(探させない)。
  const cardRef = useRef<HTMLElement | null>(null);

  const mode = transferCardMode({
    supported: transfer.supported,
    signedIn: account !== null && !account.isAnonymous,
  });

  const issue = (): void => {
    if (busy) return;
    setBusy(true);
    setError(null);
    setPanel('issue');
    void transfer
      .issue()
      .then((code) => {
        setIssued(code);
        setCopied(false);
      })
      .catch((cause: unknown) => setError(transferErrorMessage(cause)))
      .finally(() => setBusy(false));
  };

  /*
   * 旧URLの引っ越し案内から来たら、ひきつぎカードまで運ぶ。
   * **コードは自動では作らない** —— 発行は1日5回までなので、
   * 画面を開いただけで1回ぶん減らさない(押したときだけ作る)。
   */
  useEffect(() => {
    if (!openIssue) return;
    cardRef.current?.scrollIntoView({ block: 'center' });
  }, [openIssue]);

  if (mode === 'hidden') return null;
  if (mode === 'signedIn') {
    return (
      <section className="card account-card">
        <h2 className="card-title">{TRANSFER_LABELS.cardTitle}</h2>
        <p className="account-note transfer-note">{TRANSFER_LABELS.signedIn}</p>
      </section>
    );
  }

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (busy || !isCompleteCode(input)) return;
    setBusy(true);
    setError(null);
    void transfer
      .redeem(input)
      .then((result) => {
        setWelcome(welcomeBackText(result));
        setInput('');
      })
      .catch((cause: unknown) => setError(transferErrorMessage(cause)))
      .finally(() => setBusy(false));
  };

  const canCopy = typeof navigator !== 'undefined' && navigator.clipboard !== undefined;

  return (
    <section
      className={`card account-card transfer-card ${openIssue ? 'focused' : ''}`}
      ref={cardRef}
    >
      <h2 className="card-title">{TRANSFER_LABELS.cardTitle}</h2>
      <p className="account-note transfer-intro">{TRANSFER_LABELS.intro}</p>

      {welcome === null ? (
        <>
          <div className="transfer-tabs">
            <button
              type="button"
              className={`transfer-tab ${panel === 'issue' ? 'active' : ''}`}
              onClick={issue}
              disabled={busy}
            >
              {TRANSFER_LABELS.issue}
            </button>
            <button
              type="button"
              className={`transfer-tab ${panel === 'redeem' ? 'active' : ''}`}
              onClick={() => {
                setPanel('redeem');
                setError(null);
              }}
            >
              {TRANSFER_LABELS.redeem}
            </button>
          </div>

          {panel === 'issue' ? (
            <div className="transfer-panel">
              {issued ? (
                <>
                  <p className="transfer-code">{issued.code}</p>
                  <p className="account-note transfer-note">{TRANSFER_LABELS.codeHint}</p>
                  {canCopy ? (
                    <button
                      type="button"
                      className="big-button transfer-copy"
                      onClick={() => {
                        void navigator.clipboard.writeText(issued.code).then(
                          () => setCopied(true),
                          () => setCopied(false),
                        );
                      }}
                    >
                      {copied ? TRANSFER_LABELS.copied : TRANSFER_LABELS.copy}
                    </button>
                  ) : null}
                </>
              ) : (
                <p className="account-note transfer-note">{busy ? TRANSFER_LABELS.busy : ''}</p>
              )}
            </div>
          ) : null}

          {panel === 'redeem' ? (
            <form className="account-form transfer-panel" onSubmit={submit}>
              <label className="account-label" htmlFor="transfer-code">
                {TRANSFER_LABELS.inputLabel}
              </label>
              <input
                id="transfer-code"
                className="account-input transfer-input"
                type="text"
                inputMode="text"
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                value={input}
                onChange={(event) => setInput(formatCodeInput(event.target.value))}
              />
              <button
                type="submit"
                className="big-button account-submit"
                disabled={busy || !isCompleteCode(input)}
              >
                {busy ? TRANSFER_LABELS.busy : TRANSFER_LABELS.submit}
              </button>
            </form>
          ) : null}

          {error ? <p className="account-error">{error}</p> : null}
          <p className="account-note transfer-note">{TRANSFER_LABELS.notice}</p>
        </>
      ) : (
        <div className="transfer-panel">
          <p className="transfer-welcome">{welcome}</p>
          <p className="account-note transfer-note">{TRANSFER_LABELS.notice}</p>
          <button type="button" className="big-button account-submit" onClick={goTitle}>
            タイトルへ もどる
          </button>
        </div>
      )}
    </section>
  );
}

export function AccountScreen() {
  const backFromMeta = useGameStore((state) => state.backFromMeta);
  // 旧URLの案内から「ひきつぎコードを つくる」で来たか(docs/10 §3.4)。
  const accountFocus = useGameStore((state) => state.accountFocus);
  const auth = useAuthStore();
  const [tab, setTab] = useState<Tab>('register');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // ストアのアクションは生成時に固定されるので、依存に置いても再実行されない。
  useEffect(() => auth.init(), [auth.init]);

  // 「そのメールはもう使われている」→ ログインタブへ寄せる(打ち直させない)。
  useEffect(() => {
    if (!auth.suggestSignIn) return;
    setTab('signIn');
    setPassword('');
    auth.clearSuggestSignIn();
  }, [auth.suggestSignIn, auth.clearSuggestSignIn]);

  const signedIn = auth.user !== null && !auth.user.isAnonymous;
  const canSubmit = email.trim().length > 0 && password.length >= MIN_PASSWORD_LENGTH && !auth.busy;

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (!canSubmit) return;
    const action = tab === 'register' ? auth.register : auth.signIn;
    void action(email, password).then(() => setPassword(''));
  };

  return (
    <div className="screen account">
      <header className="account-header">
        <button type="button" className="back-button" onClick={backFromMeta}>
          もどる
        </button>
        <h1 className="screen-title">とうろく・ログイン</h1>
        <span className="account-spacer" />
      </header>

      {signedIn && auth.user ? (
        <section className="card account-card">
          <h2 className="card-title">🙋 ログイン ちゅう</h2>
          <p className="account-name">{accountLabel(auth.user)}</p>
          {auth.user.email && auth.user.email !== accountLabel(auth.user) ? (
            <p className="account-detail">{auth.user.email}</p>
          ) : null}
          <p className="account-detail">ログインの しかた: {providerLabel(auth.user.providers)}</p>
          <button
            type="button"
            className="big-button account-signout"
            disabled={auth.busy}
            onClick={() => void auth.signOut()}
          >
            ログアウト
          </button>
          <p className="account-note">
            ログアウトしても、この たんまつで あそんだ きろくは のこるよ。
          </p>
        </section>
      ) : (
        <section className="card account-card">
          <p className="account-guard">⚠️ ここからは おうちのひとと いっしょに やってね。</p>

          <div className="account-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'register'}
              className={`account-tab ${tab === 'register' ? 'active' : ''}`}
              onClick={() => {
                setTab('register');
                auth.clearError();
              }}
            >
              はじめて とうろく
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'signIn'}
              className={`account-tab ${tab === 'signIn' ? 'active' : ''}`}
              onClick={() => {
                setTab('signIn');
                auth.clearError();
              }}
            >
              ログイン
            </button>
          </div>

          <form className="account-form" onSubmit={submit}>
            <label className="account-label" htmlFor="account-email">
              メールアドレス
            </label>
            <input
              id="account-email"
              className="account-input"
              type="email"
              autoComplete="email"
              inputMode="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />

            <label className="account-label" htmlFor="account-password">
              パスワード({MIN_PASSWORD_LENGTH}もじ いじょう)
            </label>
            <input
              id="account-password"
              className="account-input"
              type="password"
              autoComplete={tab === 'register' ? 'new-password' : 'current-password'}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />

            <button type="submit" className="big-button account-submit" disabled={!canSubmit}>
              {tab === 'register' ? 'とうろく する' : 'ログイン する'}
            </button>
          </form>

          <p className="account-or">── または ──</p>
          <button
            type="button"
            className="big-button account-google"
            disabled={auth.busy}
            onClick={() => void auth.signInWithGoogle()}
          >
            Google で つづける
          </button>

          {auth.error ? <p className="account-error">{auth.error}</p> : null}

          <p className="account-note">
            {tab === 'register'
              ? 'いま あそんでいる きろく(★・コイン・アイテム)は そのまま ひきつがれるよ。'
              : 'べつの アカウントで ログインすると、いま あそんでいる きろくは ひきつがれないよ。'}
          </p>
        </section>
      )}

      {/* ひきつぎコード(docs/10 §3.4)。ゲストのときだけ「つくる・いれる」を出す。 */}
      <TransferCard openIssue={accountFocus === 'transfer'} />

      <p className="account-free">とうろく しなくても、ゲームは いままでどおり あそべるよ。</p>

      {auth.conflict ? (
        <div className="account-dialog-backdrop" role="dialog" aria-modal="true">
          <div className="account-dialog">
            <h2 className="card-title">この アカウントは もう つかわれています</h2>
            <p className="account-detail">
              そちらで ログインすると、いま あそんでいる きろく(★・コイン・アイテム)は
              ひきつがれません。
            </p>
            <div className="account-dialog-actions">
              <button
                type="button"
                className="big-button account-submit"
                disabled={auth.busy}
                onClick={() => void auth.useExistingAccount()}
              >
                そちらで ログインする
              </button>
              <button type="button" className="big-button back" onClick={auth.dismissConflict}>
                やめる
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
